import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Switch } from '@travellegacy/nexops-design-system';
import { usersAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { Loader2 } from 'lucide-react';
import type { User } from '@/shared/types';
import { isAxiosError } from 'axios';

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client?: User | null;
}

const clientSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
});

type ClientFormData = z.infer<typeof clientSchema>;

export function ClientModal({ isOpen, onClose, client }: ClientModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!client;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    control,
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      isActive: true,
    },
  });

  // Preencher form quando estiver editando
  useEffect(() => {
    if (client) {
      setValue('name', client.name);
      setValue('email', client.email);
      setValue('password', '');
      setValue('isActive', client.isActive);
    } else {
      reset();
    }
  }, [client, setValue, reset]);

  // Mutation para atualizar cliente
  const updateMutation = useMutation({
    mutationFn: async (data: ClientFormData) => {
      const updateData: {
        name: string;
        email: string;
        role: 'CLIENT';
        isActive: boolean;
        notionPageId?: string;
        password?: string;
      } = {
        name: data.name,
        email: data.email,
        role: 'CLIENT', // Force CLIENT role
        isActive: data.isActive,
        notionPageId: client?.notionPageId || undefined, // Preserve existing Notion Page ID
      };

      // Só incluir senha se foi preenchida
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }

      return usersAPI.update(client!.id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Sucesso', 'Cliente atualizado com sucesso!');
      reset();
      onClose();
    },
    onError: (error) => {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao atualizar cliente'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    },
  });

  const onSubmit = (data: ClientFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    }
  };

  const isLoading = updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogClose onClick={onClose} />
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-4">
            {/* Active Status */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">Status</p>
                <p className="text-sm text-muted-foreground">
                  Cliente {watch('isActive') ? 'ativo' : 'inativo'}
                </p>
              </div>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>

            <Input
              label="Nome"
              placeholder="Nome completo"
              {...register('name')}
              error={errors.name?.message}
            />

            <Input
              label="Email"
              type="email"
              placeholder="email@exemplo.com"
              {...register('email')}
              error={errors.email?.message}
            />

            <Input
              label="Nova Senha (deixe em branco para manter)"
              type="password"
              placeholder="Digite para alterar"
              {...register('password')}
              error={errors.password?.message}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
